import { useQuery } from '@tanstack/react-query';

import { FIVE_MINUTES_IN_MS } from '../services/ExternalService/constants';
import ExternalService from '../services/ExternalService/ExternalApiService';
import {
  type RuleMetadata,
  type RuleMetadataResponse,
} from '../services/types';

/**
 * Fetches the rule metadata from the external API service.
 *
 * @async
 * @function fetchRuleMetadata
 * @returns {Promise<RuleMetadata>} A promise that resolves to the rule metadata.
 */
const fetchRuleMetadata = async (): Promise<RuleMetadata> => {
  const response =
    (await ExternalService.getRulesMetadata()) as RuleMetadataResponse;

  return response.data;
};

/**
 * Custom hook to retrieve the rule metadata using React Query's `useQuery`.
 *
 * @function useGetRuleMetadata
 * @returns {import('@tanstack/react-query').UseQueryResult<RuleMetadata, unknown>} A query result object containing the rule metadata, loading state, and potential errors.
 */
const useGetRuleMetadata = () =>
  useQuery<RuleMetadata>({
    queryKey: ['RULE_METADATA'], // Unique query key
    queryFn: fetchRuleMetadata, // Function to fetch data
    staleTime: FIVE_MINUTES_IN_MS, // Cache data for 5 minutes
  });

export default useGetRuleMetadata;
